import { useState, useTransition, useCallback } from 'react';
import { EntityQueryResponseMap, NEXT_ENTITY, QUERY_VARIANT } from '@common/interfaces';
import { getTokenLocalStorage } from '@config/authentication';
import { queryNextEntityWithToken } from '@utils/serverActions/clientUtility';
import { useAppSelector } from '@store/hooks';
import { selectOrderId } from '@store/selectors';

interface IUseClientQueryOptions<MAPPED_ENTITY extends NEXT_ENTITY> {
  variables?: Record<string, any>;
  nextOptions?: Record<string, any>;
  skip?: boolean;
  onCompleted?: (data: EntityQueryResponseMap[MAPPED_ENTITY], options?: any) => void;
  onError?: (error: any, options?: any) => void;
  queryVariant?: QUERY_VARIANT;
}

interface IUseClientLazyQueryResult<MAPPED_ENTITY extends NEXT_ENTITY> {
  loading: boolean;
  data: EntityQueryResponseMap[MAPPED_ENTITY] | null;
  error: any;
}

type TUseClientLazyQueryReturn<MAPPED_ENTITY extends NEXT_ENTITY> = [
  (queryVariables?: Record<string, any>) => Promise<EntityQueryResponseMap[MAPPED_ENTITY]>,
  IUseClientLazyQueryResult<MAPPED_ENTITY>,
];

export function useClientLazyQuery<MAPPED_ENTITY extends NEXT_ENTITY>(
  entity: MAPPED_ENTITY,
  options: IUseClientQueryOptions<MAPPED_ENTITY> = {}
): TUseClientLazyQueryReturn<MAPPED_ENTITY> {
  const {
    queryVariant = QUERY_VARIANT.FULL,
    variables = {},
    nextOptions = { cache: 'no-store' },
    skip = false,
    onCompleted,
    onError,
  } = options;

  const [isPending, startTransition] = useTransition();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<EntityQueryResponseMap[MAPPED_ENTITY] | null>(null);
  const [error, setError] = useState<any>(null);
  const nextOrderId = useAppSelector(selectOrderId);
  const executeQuery = useCallback(
    async (queryVariables?: Record<string, any>) => {
      if (skip) return null;
      setLoading(true);
      try {
        const result = await queryNextEntityWithToken(
          entity,
          getTokenLocalStorage(),
          queryVariables?.variables || variables,
          queryVariant,
          nextOptions,
          nextOrderId
        );
        startTransition(() => {
          setData(result);
          setError(null);
        });

        onCompleted?.(result, queryVariables);
        return result;
      } catch (err) {
        startTransition(() => {
          setError(err);
        });

        onError?.(err as any, queryVariables);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [entity, JSON.stringify(variables), skip]
  );

  return [
    executeQuery,
    {
      loading: loading,
      data,
      error,
    },
  ];
}
